import { useState } from 'react';

/**
 * Identity stabilizer: returns the SAME `value` reference for as long as its
 * primitive `signature` is unchanged, and the fresh `value` the render its
 * signature changes.
 *
 * Use it when a memo must rebuild a non-primitive result on a high-frequency
 * dep (e.g. a 30s clock tick) but downstream consumers key memos/effects on
 * the result's identity — without this, every tick hands them fresh
 * `Set`/`Map`/array identities for byte-identical content and re-runs them
 * all (`useLiveEventsRail` → `SpacesCarousel`'s ordering memo + ring
 * impressions). The caller derives the signature from exactly the fields
 * that make the value meaningfully different.
 *
 * Implementation is React's documented "store info from previous renders"
 * pattern (the `useOptimisticToggle` house pattern): a GUARDED render-phase
 * setState on this hook's own state, self-terminating (the store makes the
 * guard false on the immediate re-render), so it is React-Compiler-safe and
 * cannot loop. A ref-compare inside the memo would be a render-phase ref
 * write — a Rules-of-React violation — which is why this exists instead.
 */
export function useStableBySignature<T>(value: T, signature: string): T {
  const [held, setHeld] = useState({ value, signature });
  if (held.signature !== signature) {
    setHeld({ value, signature });
    return value;
  }
  return held.value;
}
